import { useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { Plus, Search, X, Trash2, ExternalLink, Save, Sparkles, Copy, Mail, RefreshCw } from "lucide-react";

const STAGES = [
  { key: "new", label: "New", color: "bg-slate-500" },
  { key: "contacted", label: "Contacted", color: "bg-sky-600" },
  { key: "qualified", label: "Qualified", color: "bg-yellow-500" },
  { key: "proposal", label: "Proposal", color: "bg-[var(--tt-zam-orange)]" },
  { key: "won", label: "Won", color: "bg-[var(--tt-green-2)]" },
  { key: "lost", label: "Lost", color: "bg-red-700" },
];

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [sources, setSources] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [dragKey, setDragKey] = useState(null);

  const refresh = async () => {
    const params = {};
    if (q) params.q = q;
    if (sourceFilter) params.source = sourceFilter;
    const r = await api.listLeads(params);
    setLeads(r.data.leads || []);
  };

  useEffect(() => {
    api.listSources().then((r) => setSources(r.data.sources || []));
  }, []);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [q, sourceFilter]);

  const groups = useMemo(() => {
    const g = {};
    STAGES.forEach((s) => { g[s.key] = []; });
    leads.forEach((l) => { (g[l.stage] || g.new).push(l); });
    return g;
  }, [leads]);

  const onDrop = async (stage) => {
    if (!dragKey) return;
    const lead = leads.find((l) => l.id === dragKey);
    if (!lead || lead.stage === stage) { setDragKey(null); return; }
    await api.updateLead(dragKey, { stage });
    setDragKey(null);
    refresh();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold">Leads</h1>
          <p className="text-white/60 mt-1 text-sm">Drag cards between stages to update.</p>
        </div>
        <button data-testid="admin-new-lead-btn" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--tt-green)] hover:bg-[var(--tt-green-dark)] px-4 py-2 text-sm font-semibold transition">
          <Plus className="h-4 w-4" /> New lead
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 rounded-lg bg-[#0f1512] border border-white/10 px-3 py-2">
          <Search className="h-4 w-4 text-white/40" />
          <input
            data-testid="admin-leads-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search company, name, email…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/40"
          />
        </div>
        <select
          data-testid="admin-leads-source-filter"
          value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
          className="rounded-lg bg-[#0f1512] border border-white/10 px-3 py-2 text-sm"
        >
          <option value="">All sources</option>
          {sources.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {STAGES.map((s) => (
          <div
            key={s.key}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(s.key)}
            data-testid={`kanban-col-${s.key}`}
            className="rounded-xl bg-[#0f1512] border border-white/10 flex flex-col min-h-[300px]"
          >
            <div className="px-3 py-2.5 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${s.color}`} />
                <span className="text-xs font-semibold uppercase tracking-wider text-white/70">{s.label}</span>
              </div>
              <span className="text-[10px] rounded-full bg-white/5 px-2 py-0.5 text-white/60">{groups[s.key].length}</span>
            </div>
            <div className="p-2 space-y-2 flex-1 overflow-y-auto max-h-[70vh]">
              {groups[s.key].map((l) => (
                <div
                  key={l.id}
                  draggable
                  onDragStart={() => setDragKey(l.id)}
                  onClick={() => setSelected(l.id)}
                  data-testid={`lead-card-${l.id}`}
                  className="rounded-lg bg-[#131a17] border border-white/10 p-3 cursor-pointer hover:border-[var(--tt-green)] transition"
                >
                  <div className="text-sm font-semibold truncate">{l.company || "(no company)"}</div>
                  {l.contact_name && <div className="text-xs text-white/60 mt-0.5 truncate">{l.contact_name}{l.title ? ` · ${l.title}` : ""}</div>}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] uppercase tracking-wider text-white/40">{l.source}</span>
                    {l.deal_value > 0 && <span className="text-[11px] font-semibold text-[var(--tt-zam-orange)]">${(l.deal_value || 0).toLocaleString()}</span>}
                  </div>
                </div>
              ))}
              {groups[s.key].length === 0 && (
                <div className="text-xs text-white/30 text-center py-6">Drop leads here</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showCreate && <LeadModal sources={sources} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refresh(); }} />}
      {selected && <LeadDetail id={selected} sources={sources} onClose={() => setSelected(null)} onChanged={refresh} />}
    </div>
  );
};

const LeadModal = ({ sources, onClose, onSaved, initial = null }) => {
  const [form, setForm] = useState(initial || {
    company: "", contact_name: "", title: "", email: "", phone: "",
    linkedin_url: "", source: "manual", stage: "new", deal_value: 0,
    monthly_volume: 0, marketplaces: "", notes: "", tags: [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.company.trim()) return;
    setSaving(true);
    try {
      if (initial?.id) await api.updateLead(initial.id, form);
      else await api.createLead(form);
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} title={initial ? "Edit lead" : "New lead"}>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input label="Company *" value={form.company} onChange={(v) => set("company", v)} testid="lead-field-company" />
        <Input label="Contact name" value={form.contact_name} onChange={(v) => set("contact_name", v)} />
        <Input label="Title" value={form.title} onChange={(v) => set("title", v)} />
        <Input label="Email" value={form.email} onChange={(v) => set("email", v)} type="email" />
        <Input label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
        <Input label="LinkedIn URL" value={form.linkedin_url} onChange={(v) => set("linkedin_url", v)} />
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-white/50">Source</span>
          <select value={form.source} onChange={(e) => set("source", e.target.value)} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm">
            {sources.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-white/50">Stage</span>
          <select value={form.stage} onChange={(e) => set("stage", e.target.value)} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm">
            {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
        <Input label="Deal value (USD)" value={form.deal_value} onChange={(v) => set("deal_value", Number(v) || 0)} type="number" />
        <Input label="Monthly order volume" value={form.monthly_volume} onChange={(v) => set("monthly_volume", Number(v) || 0)} type="number" />
        <div className="sm:col-span-2">
          <Input label="Marketplaces" value={form.marketplaces} onChange={(v) => set("marketplaces", v)} placeholder="Amazon, Shopify, Walmart…" />
        </div>
        <div className="sm:col-span-2">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-white/50">Notes</span>
            <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm resize-none" />
          </label>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-white/60 hover:text-white transition">Cancel</button>
        <button data-testid="lead-save-btn" onClick={save} disabled={saving || !form.company.trim()} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--tt-green)] hover:bg-[var(--tt-green-dark)] px-4 py-2 text-sm font-semibold transition disabled:opacity-50">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save lead"}
        </button>
      </div>
    </ModalShell>
  );
};

const LeadDetail = ({ id, sources, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [showDraft, setShowDraft] = useState(false);

  const load = async () => {
    const r = await api.getLead(id);
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <ModalShell onClose={onClose} title="Loading…"><div className="text-white/60 text-sm">Loading…</div></ModalShell>;

  const l = data.lead;

  if (editing) {
    return <LeadModal sources={sources} initial={l} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); onChanged(); }} />;
  }

  const addNote = async () => {
    if (!note.trim()) return;
    await api.addActivity(id, { kind: "note", content: note });
    setNote("");
    load();
  };

  const del = async () => {
    if (!window.confirm(`Delete lead "${l.company}"? This cannot be undone.`)) return;
    await api.deleteLead(id);
    onClose();
    onChanged();
  };

  return (
    <ModalShell onClose={onClose} title={l.company}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Cell label="Contact" v={l.contact_name} />
          <Cell label="Title" v={l.title} />
          <Cell label="Email" v={l.email} />
          <Cell label="Phone" v={l.phone} />
          <Cell label="Source" v={l.source} />
          <Cell label="Stage" v={l.stage} />
          <Cell label="Deal value" v={`$${(l.deal_value || 0).toLocaleString()}`} />
          <Cell label="Monthly volume" v={l.monthly_volume ? l.monthly_volume.toLocaleString() : "—"} />
          <div className="col-span-2"><Cell label="Marketplaces" v={l.marketplaces} /></div>
          {l.linkedin_url && (
            <div className="col-span-2">
              <span className="text-[11px] uppercase tracking-wider text-white/50">LinkedIn</span>
              <a href={l.linkedin_url} target="_blank" rel="noreferrer" className="mt-1 flex items-center gap-1 text-sm text-[var(--tt-zam-orange)] hover:underline break-all">
                {l.linkedin_url} <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
          {l.notes && (
            <div className="col-span-2">
              <span className="text-[11px] uppercase tracking-wider text-white/50">Notes</span>
              <div className="mt-1 text-sm text-white/80 whitespace-pre-wrap">{l.notes}</div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-white/10">
          <button data-testid="lead-draft-email-btn" onClick={() => setShowDraft(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--tt-zam-orange)] hover:bg-[#d16600] px-3 py-2 text-sm font-semibold transition">
            <Sparkles className="h-4 w-4" /> AI draft email
          </button>
          <button onClick={() => setEditing(true)} className="rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2 text-sm font-semibold transition">Edit</button>
          <button onClick={del} className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 px-3 py-2 text-sm transition">
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>

        {showDraft && <AiDraftPanel leadId={id} onClose={() => setShowDraft(false)} onLogged={load} />}

        <div>
          <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Add note</div>
          <div className="flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Quick note or call log…" className="flex-1 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" onKeyDown={(e) => { if (e.key === "Enter") addNote(); }} />
            <button onClick={addNote} disabled={!note.trim()} className="rounded-lg bg-[var(--tt-green)] hover:bg-[var(--tt-green-dark)] px-3 text-sm font-semibold disabled:opacity-40">Add</button>
          </div>
        </div>

        {data.activities.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Activity</div>
            <ul className="space-y-2 max-h-52 overflow-y-auto">
              {data.activities.map((a) => (
                <li key={a.id} className="text-xs bg-white/5 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-white/50 mb-1">
                    <span className="uppercase tracking-wider">{a.kind.replace("_", " ")}</span>
                    <span>·</span>
                    <span>{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-white/80">{a.content}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

const ModalShell = ({ children, onClose, title }) => (
  <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
    <div className="w-full max-w-2xl bg-[#0f1512] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-xl font-bold">{title}</h2>
        <button onClick={onClose} className="text-white/50 hover:text-white transition"><X className="h-5 w-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

const Input = ({ label, value, onChange, type = "text", placeholder, testid }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-white/50">{label}</span>
    <input
      data-testid={testid}
      type={type} value={value} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[var(--tt-green)] transition"
    />
  </label>
);

const Cell = ({ label, v }) => (
  <div>
    <span className="text-[11px] uppercase tracking-wider text-white/50">{label}</span>
    <div className="mt-1 text-white/85">{v || "—"}</div>
  </div>
);

const KINDS = [
  { key: "intro", label: "Intro (cold)" },
  { key: "followup1", label: "Follow-up 1 (day 3)" },
  { key: "followup2", label: "Follow-up 2 (day 7)" },
  { key: "meeting_request", label: "Meeting request" },
];
const TONES = [
  { key: "professional", label: "Professional" },
  { key: "friendly", label: "Friendly" },
  { key: "direct", label: "Direct" },
  { key: "casual", label: "Casual" },
];

const AiDraftPanel = ({ leadId, onClose, onLogged }) => {
  const [kind, setKind] = useState("intro");
  const [tone, setTone] = useState("professional");
  const [customPrompt, setCustomPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [draft, setDraft] = useState(null);
  const [copied, setCopied] = useState("");

  const generate = async () => {
    setBusy(true); setErr(""); setDraft(null);
    try {
      const r = await api.draftEmail(leadId, { kind, tone, custom_prompt: customPrompt });
      setDraft(r.data);
      onLogged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const copy = (val, label) => {
    navigator.clipboard.writeText(val);
    setCopied(label);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className="mt-4 rounded-xl border border-[var(--tt-zam-orange)]/30 bg-[var(--tt-zam-orange)]/5 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[var(--tt-zam-orange)]" />
          <span className="font-semibold text-[var(--tt-zam-orange)]">AI email draft</span>
          <span className="text-[10px] uppercase tracking-widest text-white/40">Claude Sonnet 4.5</span>
        </div>
        <button onClick={onClose} className="text-white/40 hover:text-white transition"><X className="h-4 w-4" /></button>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/50">Kind</span>
          <select data-testid="ai-draft-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-2.5 py-2 text-sm">
            {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/50">Tone</span>
          <select data-testid="ai-draft-tone" value={tone} onChange={(e) => setTone(e.target.value)} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-2.5 py-2 text-sm">
            {TONES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </label>
      </div>
      <label className="block mb-3">
        <span className="text-[10px] uppercase tracking-wider text-white/50">Extra instruction (optional)</span>
        <input value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)} placeholder="e.g. Mention their recent expansion into Africa" className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-2.5 py-2 text-sm" />
      </label>

      <button
        onClick={generate}
        disabled={busy}
        data-testid="ai-draft-generate"
        className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--tt-zam-orange)] hover:bg-[#d16600] px-4 py-2 text-sm font-semibold transition disabled:opacity-50"
      >
        {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {busy ? "Drafting…" : draft ? "Re-generate" : "Generate email"}
      </button>

      {err && <div className="mt-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-xs p-3">{err}</div>}

      {draft && (
        <div className="mt-4 space-y-3">
          <div className="rounded-lg bg-black/40 border border-white/10 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider text-white/50">Subject</span>
              <button onClick={() => copy(draft.subject, "subject")} className="text-[11px] text-white/60 hover:text-white inline-flex items-center gap-1">
                <Copy className="h-3 w-3" /> {copied === "subject" ? "Copied!" : "Copy"}
              </button>
            </div>
            <div className="text-sm font-semibold text-white">{draft.subject}</div>
          </div>

          <div className="rounded-lg bg-black/40 border border-white/10 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider text-white/50">Body</span>
              <div className="flex items-center gap-3">
                <button onClick={() => copy(draft.body, "body")} className="text-[11px] text-white/60 hover:text-white inline-flex items-center gap-1">
                  <Copy className="h-3 w-3" /> {copied === "body" ? "Copied!" : "Copy body"}
                </button>
                <button onClick={() => copy(`Subject: ${draft.subject}\n\n${draft.body}`, "all")} className="text-[11px] text-white/60 hover:text-white inline-flex items-center gap-1">
                  <Copy className="h-3 w-3" /> {copied === "all" ? "Copied!" : "Copy all"}
                </button>
              </div>
            </div>
            <pre className="text-sm text-white/90 whitespace-pre-wrap font-sans leading-relaxed">{draft.body}</pre>
          </div>

          <div className="flex items-center gap-2">
            <a
              href={`mailto:?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-2 text-sm font-semibold transition"
              data-testid="ai-draft-open-mail"
            >
              <Mail className="h-3.5 w-3.5" /> Open in mail
            </a>
            <button
              data-testid="ai-approve-send"
              onClick={async () => {
                try {
                  await api.approveEmail(leadId, { subject: draft.subject, body: draft.body, kind, tone });
                  onLogged();
                  onClose();
                  alert("Approved. Intro queued for send; follow-ups scheduled for day 3 and day 10. Track in Outbox.");
                } catch (e) {
                  alert(e?.response?.data?.detail || e.message);
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--tt-green)] hover:bg-[var(--tt-green-dark)] px-4 py-2 text-sm font-semibold transition"
            >
              <Sparkles className="h-3.5 w-3.5" /> Approve &amp; auto-send
            </button>
            <span className="text-[11px] text-white/40">Auto-schedules 2 follow-ups.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLeads;
